import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings, TrendingUp, TrendingDown, Minus, AlertCircle, Info, BarChart3, Activity } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { SSRSettingsDialog } from '@/components/SSRSettingsDialog';
import { getIndicatorInfo } from '@/content/indicators';
import { 
  useSSRAlpha, 
  SSRConfig, 
  DEFAULT_SSR_CONFIG,
  getSignalColor,
  getSignalBgColor,
  getSignalLabel,
  getSignalInterpretation,
  getRsiZoneColor
} from '@/hooks/useSSRAlpha';
import { 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  ReferenceLine, 
  Area, 
  Line,
  ComposedChart,
  ReferenceArea,
  Brush
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const STORAGE_KEY = 'ssr-alpha-config';
const MODE_STORAGE_KEY = 'ssr-alpha-display-mode';

const CONFIG_VERSION = 2;
const LEGACY_OVERSOLD_DEFAULT = 25;

type DisplayMode = 'rsi' | 'bb';

function loadConfig(): SSRConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, unknown>;
      const merged = { ...DEFAULT_SSR_CONFIG, ...(parsed as Partial<SSRConfig>) };

      // Migration: if user had the legacy default oversold (25), migrate to 35.
      // Do not override user custom values.
      const storedVersion = typeof parsed.configVersion === 'number' ? parsed.configVersion : 0;
      const needsMigration = storedVersion < CONFIG_VERSION && merged.rsiOversold === LEGACY_OVERSOLD_DEFAULT;

      if (needsMigration) {
        const migrated: SSRConfig = { ...merged, rsiOversold: DEFAULT_SSR_CONFIG.rsiOversold };
        // Persist migrated version while keeping stored shape backward-compatible.
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...parsed, ...migrated, configVersion: CONFIG_VERSION })
        );
        return migrated;
      }

      return merged;
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_SSR_CONFIG;
}

function saveConfig(config: SSRConfig) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage errors
  }
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; dataKey: string; color: string }>;
  label?: number;
  showBB?: boolean;
}

function BtcPriceTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length || !label) return null;
  
  const formattedDate = format(new Date(label), 'dd MMM yyyy', { locale: ptBR });
  const btcPrice = payload.find(p => p.dataKey === 'btcPrice')?.value;
  const ssrRsi = payload.find(p => p.dataKey === 'ssrRsi')?.value;
  
  return (
    <div className="bg-popover/95 backdrop-blur border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{formattedDate}</p>
      {btcPrice !== undefined && (
        <p className="text-sm font-medium text-amber-500">
          BTC: ${btcPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}
        </p>
      )}
      {ssrRsi !== undefined && (
        <p className="text-xs text-muted-foreground">
          RSI: {ssrRsi.toFixed(1)}
        </p>
      )}
    </div>
  );
}

function RsiTooltip({ active, payload, label, showBB }: ChartTooltipProps) {
  if (!active || !payload?.length || !label) return null;
  
  const formattedDate = format(new Date(label), 'dd MMM yyyy', { locale: ptBR });
  const ssrRsi = payload.find(p => p.dataKey === 'ssrRsi')?.value;
  const ratio = payload.find(p => p.dataKey === 'ratio')?.value;
  const upper = payload.find(p => p.dataKey === 'upper')?.value;
  const lower = payload.find(p => p.dataKey === 'lower')?.value;
  
  return (
    <div className="bg-popover/95 backdrop-blur border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{formattedDate}</p>
      {ssrRsi !== undefined && (
        <p className="text-sm font-medium text-amber-500">RSI: {ssrRsi.toFixed(1)}</p>
      )}
      {showBB && ratio !== undefined && (
        <p className="text-sm text-primary">Ratio: {ratio.toFixed(3)}</p>
      )}
      {showBB && upper !== undefined && (
        <p className="text-sm text-red-500">Upper: {upper.toFixed(3)}</p>
      )}
      {showBB && lower !== undefined && (
        <p className="text-sm text-emerald-500">Lower: {lower.toFixed(3)}</p>
      )}
    </div>
  );
}

// Calculate signal zones from history for BTC chart background coloring
interface SignalZone {
  startDate: number;
  endDate: number;
  type: 'overbought' | 'oversold';
}

export function SSRAlphaCard() {
  const [config, setConfig] = useState<SSRConfig>(loadConfig);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState<DisplayMode>(() => {
    try {
      const stored = localStorage.getItem(MODE_STORAGE_KEY);
      return (stored === 'bb' || stored === 'rsi') ? stored : 'rsi';
    } catch {
      return 'rsi';
    }
  });
  
  const { data, isLoading, error } = useSSRAlpha(config);

  useEffect(() => {
    saveConfig(config);
  }, [config]);

  useEffect(() => {
    try {
      localStorage.setItem(MODE_STORAGE_KEY, displayMode);
    } catch {
      // Ignore storage errors
    }
  }, [displayMode]);

  const handleConfigChange = (newConfig: SSRConfig) => {
    setConfig(newConfig);
  };

  // Get indicator description
  const ssrInfo = getIndicatorInfo('SSRAlpha');

  // Calculate signal zones for BTC chart background based on display mode
  const signalZones = useMemo<SignalZone[]>(() => {
    if (!data?.history) return [];
    
    const zones: SignalZone[] = [];
    let currentZone: { startDate: number; type: 'overbought' | 'oversold' } | null = null;
    
    for (const point of data.history) {
      let zoneType: 'overbought' | 'oversold' | null = null;
      
      if (displayMode === 'bb' && point.upper !== null && point.lower !== null) {
        // Bollinger Bands mode
        zoneType = 
          point.ratio > point.upper ? 'overbought' :
          point.ratio < point.lower ? 'oversold' : null;
      } else {
        // RSI mode
        const rsi = point.ssrRsi;
        if (rsi !== undefined && rsi !== null) {
          zoneType = 
            rsi >= config.rsiOverbought ? 'overbought' :
            rsi <= config.rsiOversold ? 'oversold' : null;
        }
      }
      
      if (zoneType) {
        if (!currentZone || currentZone.type !== zoneType) {
          // Close previous zone if exists
          if (currentZone) {
            zones.push({ 
              startDate: currentZone.startDate, 
              endDate: point.date, 
              type: currentZone.type 
            });
          }
          // Start new zone
          currentZone = { startDate: point.date, type: zoneType };
        }
      } else {
        // Close current zone if exiting a signal zone
        if (currentZone) {
          zones.push({ 
            startDate: currentZone.startDate, 
            endDate: point.date, 
            type: currentZone.type 
          });
          currentZone = null;
        }
      }
    }
    
    // Close any remaining zone
    if (currentZone && data.history.length > 0) {
      zones.push({ 
        startDate: currentZone.startDate, 
        endDate: data.history[data.history.length - 1].date, 
        type: currentZone.type 
      });
    }
    
    return zones;
  }, [data?.history, config.rsiOverbought, config.rsiOversold, displayMode]);

  if (isLoading) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">SSR Alpha Signal</CardTitle>
            <Skeleton className="h-8 w-8" />
          </div>
          <CardDescription>BTC Stablecoin Supply Ratio / RSI</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">SSR Alpha Signal</CardTitle>
            <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)}>
              <Settings className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>BTC Stablecoin Supply Ratio / RSI</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
            <AlertCircle className="h-8 w-8 mb-2" />
            <p className="text-sm">Erro ao carregar dados do SSR</p>
            <p className="text-xs mt-1">Tente novamente mais tarde</p>
          </div>
        </CardContent>
        <SSRSettingsDialog 
          open={settingsOpen} 
          onOpenChange={setSettingsOpen}
          config={config}
          onConfigChange={handleConfigChange}
        />
      </Card>
    );
  }

  const current = data?.current;
  const history = data?.history || [];
  const hasEnoughForBB = data?.hasEnoughForBB || false;
  const signal = current?.signal || 'neutral';
  const signalSource = current?.signalSource || 'rsi';
  
  const SignalIcon = signal === 'overbought' ? TrendingDown : signal === 'oversold' ? TrendingUp : Minus;

  // Calculate min/max for BTC price Y-axis
  const btcPrices = history.map(h => h.btcPrice).filter(p => p > 0);
  const btcMin = Math.min(...btcPrices) * 0.95;
  const btcMax = Math.max(...btcPrices) * 1.05;

  return (
    <Card className={`bg-card/50 backdrop-blur border-border/50 ${getSignalBgColor(signal)}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">SSR Alpha Signal</CardTitle>
            <Badge variant="outline" className="font-mono text-xs">
              {config.symbol}
            </Badge>
          </div>
          <div className="flex items-center gap-1">
            {/* Toggle RSI / Bollinger Bands */}
            <div className="flex items-center bg-muted/50 rounded-md p-0.5">
              <Button
                variant={displayMode === 'rsi' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setDisplayMode('rsi')}
              >
                <Activity className="h-3 w-3 mr-1" />
                RSI
              </Button>
              <Button
                variant={displayMode === 'bb' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setDisplayMode('bb')}
                disabled={!hasEnoughForBB}
                title={!hasEnoughForBB ? `Necessário ${config.bbLength} dias de dados para Bollinger Bands` : ''}
              >
                <BarChart3 className="h-3 w-3 mr-1" />
                BB
              </Button>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)}>
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          BTC Stablecoin Supply Ratio com {displayMode === 'bb' ? 'Bandas de Bollinger' : `RSI ${config.rsiLength}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Values Row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">RSI {config.rsiLength}</p>
            <p className={`text-xl font-bold font-mono ${getRsiZoneColor(current?.ssrRsi ?? null, config.rsiOverbought, config.rsiOversold)}`}>
              {current?.ssrRsi?.toFixed(1) || '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Ratio</p>
            <p className="text-lg font-mono">{current?.ratio?.toFixed(3) || '—'}</p>
          </div>
          {hasEnoughForBB && (
            <>
              <div>
                <p className="text-xs text-muted-foreground">Upper Band</p>
                <p className="text-lg font-mono text-red-500">{current?.upper?.toFixed(3) || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Lower Band</p>
                <p className="text-lg font-mono text-emerald-500">{current?.lower?.toFixed(3) || '—'}</p>
              </div>
            </>
          )}
          <div>
            <p className="text-xs text-muted-foreground">Sinal</p>
            <div className={`flex items-center gap-1 ${getSignalColor(signal)}`}>
              <SignalIcon className="h-4 w-4" />
              <span className="font-semibold text-sm">{getSignalLabel(signal)}</span>
            </div>
          </div>
        </div>

        {/* Top Panel: BTC Price Chart with colored signal zones */}
        <div className="border-t border-border/50 pt-4">
          <p className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
            <span className="inline-block w-3 h-0.5 bg-amber-500"></span>
            BITCOIN: PRICE (USD)
            <span className="mx-2">|</span>
            <span className="inline-block w-3 h-3 rounded bg-red-500/20 border border-red-500/40"></span>
            <span className="text-red-500/70">Overbought</span>
            <span className="inline-block w-3 h-3 rounded bg-emerald-500/20 border border-emerald-500/40 ml-2"></span>
            <span className="text-emerald-500/70">Oversold</span>
          </p>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={history} margin={{ top: 5, right: 50, left: 0, bottom: 0 }} syncId="ssr-alpha">
                <defs>
                  <linearGradient id="btcPriceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                
                {/* Signal zones - Overbought (Red) and Oversold (Green) */}
                {signalZones.map((zone, i) => (
                  <ReferenceArea
                    key={`zone-${i}`}
                    x1={zone.startDate}
                    x2={zone.endDate}
                    fill={zone.type === 'overbought' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)'}
                    fillOpacity={1}
                  />
                ))}
                
                <XAxis
                  dataKey="date"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(ts) => format(new Date(ts), 'MMM yy', { locale: ptBR })}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[btcMin, btcMax]}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  orientation="right"
                  width={50}
                />
                <Tooltip content={<BtcPriceTooltip />} />
                
                {/* BTC Price Area */}
                <Area
                  type="monotone"
                  dataKey="btcPrice"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fill="url(#btcPriceGradient)"
                  name="BTC Price"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bottom Panel: RSI or Bollinger Bands based on display mode */}
        <div className="border-t border-border/50 pt-4">
          {displayMode === 'rsi' ? (
            <>
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
                <span className="inline-block w-3 h-0.5 bg-amber-400"></span>
                SSR RSI {config.rsiLength}
                <span className="mx-2">|</span>
                <span className="text-red-500/70">Overbought &gt; {config.rsiOverbought}</span>
                <span className="text-emerald-500/70 ml-2">Oversold &lt; {config.rsiOversold}</span>
              </p>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={history} margin={{ top: 5, right: 50, left: 0, bottom: 40 }} syncId="ssr-alpha">
                    <defs>
                      <linearGradient id="rsiGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#fbbf24" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={(ts) => format(new Date(ts), 'MMM yy', { locale: ptBR })}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 100]}
                      ticks={[0, 25, 50, 75, 100]}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      orientation="right"
                      width={35}
                    />
                    <Tooltip content={<RsiTooltip showBB={false} />} />
                    
                    {/* Overbought zone background */}
                    <ReferenceArea
                      y1={config.rsiOverbought}
                      y2={100}
                      fill="rgba(239, 68, 68, 0.1)"
                      fillOpacity={1}
                    />
                    
                    {/* Oversold zone background */}
                    <ReferenceArea
                      y1={0}
                      y2={config.rsiOversold}
                      fill="rgba(16, 185, 129, 0.1)"
                      fillOpacity={1}
                    />
                    
                    {/* Overbought reference line */}
                    <ReferenceLine
                      y={config.rsiOverbought}
                      stroke="#ef4444"
                      strokeDasharray="4 4"
                      strokeOpacity={0.7}
                    />
                    
                    {/* Middle line (50) */}
                    <ReferenceLine
                      y={50}
                      stroke="hsl(var(--muted-foreground))"
                      strokeDasharray="2 2"
                      strokeOpacity={0.3}
                    />
                    
                    {/* Oversold reference line */}
                    <ReferenceLine
                      y={config.rsiOversold}
                      stroke="#10b981"
                      strokeDasharray="4 4"
                      strokeOpacity={0.7}
                    />
                    
                    {/* RSI Line */}
                    <Area
                      type="monotone"
                      dataKey="ssrRsi"
                      stroke="#fbbf24"
                      strokeWidth={2}
                      fill="url(#rsiGradient)"
                      name="SSR RSI"
                      connectNulls
                    />
                    
                    {/* Brush inside the chart for proper control */}
                    <Brush
                      dataKey="date"
                      height={30}
                      stroke="#fbbf24"
                      fill="hsl(215 20% 16%)"
                      travellerWidth={10}
                      tickFormatter={(ts) => format(new Date(ts), 'MMM yy', { locale: ptBR })}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
                <span className="inline-block w-3 h-0.5 bg-primary"></span>
                SSR Ratio
                <span className="mx-2">|</span>
                <span className="inline-block w-3 h-0.5 bg-red-500"></span>
                <span className="text-red-500/70">Upper BB ({config.bbUpperMult}σ)</span>
                <span className="inline-block w-3 h-0.5 bg-emerald-500 ml-2"></span>
                <span className="text-emerald-500/70">Lower BB ({config.bbLowerMult}σ)</span>
              </p>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={history} margin={{ top: 5, right: 50, left: 0, bottom: 40 }} syncId="ssr-alpha">
                    <defs>
                      <linearGradient id="ratioGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={(ts) => format(new Date(ts), 'MMM yy', { locale: ptBR })}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      domain={['auto', 'auto']}
                      tickFormatter={(v) => v.toFixed(2)}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      orientation="right"
                      width={45}
                    />
                    <Tooltip content={<RsiTooltip showBB={true} />} />
                    
                    {/* Upper Bollinger Band */}
                    <Line
                      type="monotone"
                      dataKey="upper"
                      stroke="#ef4444"
                      strokeWidth={1.5}
                      strokeDasharray="4 2"
                      dot={false}
                      name="Upper BB"
                      connectNulls
                    />
                    
                    {/* Lower Bollinger Band */}
                    <Line
                      type="monotone"
                      dataKey="lower"
                      stroke="#10b981"
                      strokeWidth={1.5}
                      strokeDasharray="4 2"
                      dot={false}
                      name="Lower BB"
                      connectNulls
                    />
                    
                    {/* Basis/Middle Line */}
                    <Line
                      type="monotone"
                      dataKey="basis"
                      stroke="hsl(var(--muted-foreground))"
                      strokeWidth={1}
                      strokeDasharray="2 2"
                      strokeOpacity={0.5}
                      dot={false}
                      name="Basis"
                      connectNulls
                    />
                    
                    {/* SSR Ratio Line */}
                    <Area
                      type="monotone"
                      dataKey="ratio"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      fill="url(#ratioGradient)"
                      name="SSR Ratio"
                      connectNulls
                    />
                    
                    {/* Brush inside the chart for proper control */}
                    <Brush
                      dataKey="date"
                      height={30}
                      stroke="hsl(var(--primary))"
                      fill="hsl(215 20% 16%)"
                      travellerWidth={10}
                      tickFormatter={(ts) => format(new Date(ts), 'MMM yy', { locale: ptBR })}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>

        {/* Interpretation */}
        <div className="text-sm text-muted-foreground border-t border-border/50 pt-3">
          <p>{getSignalInterpretation(signal)}</p>
          {!hasEnoughForBB && displayMode === 'rsi' && (
            <p className="text-xs mt-1 text-amber-500/80">
              ⚠️ Dados insuficientes para Bollinger Bands ({config.bbLength} dias). Bollinger Bands desabilitado.
            </p>
          )}
        </div>

        {/* Indicator Description */}
        <div className="border-t border-border/50 pt-3 space-y-2">
          <p className="text-sm font-medium flex items-center gap-2">
            <Info className="h-4 w-4" />
            {ssrInfo.title}
          </p>
          {ssrInfo.description.map((line, i) => (
            <div key={i} className="text-xs text-muted-foreground leading-relaxed prose prose-sm dark:prose-invert prose-strong:text-muted-foreground prose-strong:font-semibold max-w-none">
              <ReactMarkdown>{line}</ReactMarkdown>
            </div>
          ))}
        </div>
      </CardContent>

      <SSRSettingsDialog 
        open={settingsOpen} 
        onOpenChange={setSettingsOpen}
        config={config}
        onConfigChange={handleConfigChange}
      />
    </Card>
  );
}
